import { saveAs } from 'file-saver';
import imageCompression from 'browser-image-compression';

/**
 * Reads a file as an ArrayBuffer
 * @param {File} file - The file to read
 * @returns {Promise<ArrayBuffer>} - The file data as ArrayBuffer
 */
export const readFileAsArrayBuffer = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file provided'));
      return;
    }
    
    const reader = new FileReader();
    
    reader.onload = () => {
      resolve(reader.result);
    };
    
    reader.onerror = (error) => {
      reject(error);
    };
    
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Reads a text file as a string
 * @param {File} file - The text file to read
 * @returns {Promise<string>} - The file content as string
 */
export const readFileAsText = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file provided'));
      return;
    }
    
    const reader = new FileReader();
    
    reader.onload = (event) => {
      resolve(event.target.result);
    };
    
    reader.onerror = (error) => {
      reject(error);
    };
    
    reader.readAsText(file);
  });
};

/**
 * Downloads data as a file
 * @param {Blob|string} data - The data to download
 * @param {string} filename - The filename for the download
 * @returns {void}
 */
export const downloadFile = (data, filename) => {
  if (!data) {
    console.error('No data provided for download');
    return;
  }
  
  saveAs(data, filename);
};

/**
 * Compresses an image file
 * @param {File} imageFile - The image file to compress
 * @param {Object} options - Compression options
 * @returns {Promise<File>} - The compressed image file
 */
export const compressImage = async (imageFile, options = {}) => {
  const defaultOptions = {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    useWebWorker: true
  };
  
  try {
    return await imageCompression(imageFile, { ...defaultOptions, ...options });
  } catch (error) {
    console.error('Image compression error:', error);
    throw error;
  }
};

/**
 * Extracts file extension from a filename
 * @param {string} filename - The filename
 * @returns {string} - The file extension
 */
export const getFileExtension = (filename) => {
  if (!filename || typeof filename !== 'string') return '';
  return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase();
};

/**
 * Gets mime type from file extension
 * @param {string} extension - The file extension
 * @returns {string} - The mime type
 */
export const getMimeType = (extension) => {
  const mimeTypes = {
    'png': 'image/png',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'gif': 'image/gif',
    'pdf': 'application/pdf',
    'txt': 'text/plain'
  };
  
  return mimeTypes[extension.toLowerCase()] || 'application/octet-stream';
};